import { 
  Calculator, 
  Shield, 
  TrendingDown, 
  FileText, 
  Target, 
  Award,
  Sparkles,
  Zap,
  Library,
  GraduationCap,
  Flame,
  Lock
} from "lucide-react";

const BADGE_ICONS = {
  calculator: Calculator,
  shield: Shield,
  "trending-down": TrendingDown,
  "file-text": FileText,
  target: Target,
  award: Award,
  sparkles: Sparkles,
  zap: Zap,
  library: Library,
  "graduation-cap": GraduationCap,
  flame: Flame,
};

const BADGE_COLORS = {
  emerald: "from-emerald-400 to-emerald-600",
  blue: "from-blue-400 to-blue-600",
  purple: "from-purple-400 to-purple-600",
  amber: "from-amber-400 to-amber-600",
  rose: "from-rose-400 to-rose-600",
  cyan: "from-cyan-400 to-cyan-600",
  yellow: "from-yellow-400 to-yellow-500",
  orange: "from-orange-400 to-orange-600",
  indigo: "from-indigo-400 to-indigo-600",
  red: "from-red-400 to-red-600",
};

const BADGE_BG_COLORS = {
  emerald: "bg-emerald-100 dark:bg-emerald-900/30",
  blue: "bg-blue-100 dark:bg-blue-900/30",
  purple: "bg-purple-100 dark:bg-purple-900/30",
  amber: "bg-amber-100 dark:bg-amber-900/30",
  rose: "bg-rose-100 dark:bg-rose-900/30",
  cyan: "bg-cyan-100 dark:bg-cyan-900/30",
  yellow: "bg-yellow-100 dark:bg-yellow-900/30",
  orange: "bg-orange-100 dark:bg-orange-900/30",
  indigo: "bg-indigo-100 dark:bg-indigo-900/30",
  red: "bg-red-100 dark:bg-red-900/30",
};

export const BadgeCard = ({ badge, earned = false, showDate = false, size = "md" }) => {
  const Icon = BADGE_ICONS[badge.icon] || Award;
  const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.emerald;
  const bgClass = BADGE_BG_COLORS[badge.color] || BADGE_BG_COLORS.emerald;
  
  const sizeClasses = {
    sm: {
      container: "w-16 h-16",
      icon: "w-6 h-6",
      badge: "w-12 h-12"
    },
    md: {
      container: "w-20 h-20",
      icon: "w-8 h-8",
      badge: "w-14 h-14"
    },
    lg: {
      container: "w-24 h-24",
      icon: "w-10 h-10",
      badge: "w-18 h-18"
    }
  };
  
  const sizes = sizeClasses[size];

  if (!earned) {
    return (
      <div className="flex flex-col items-center gap-2 opacity-50">
        <div className={`${sizes.container} rounded-2xl bg-muted flex items-center justify-center relative`}>
          <Lock className={`${sizes.icon} text-muted-foreground`} />
        </div>
        <div className="text-center">
          <p className="text-xs font-medium text-muted-foreground">{badge.name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 group">
      <div className={`${sizes.container} rounded-2xl ${bgClass} flex items-center justify-center relative overflow-hidden transition-transform group-hover:scale-105`}>
        {/* Gradient glow effect */}
        <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-20`} />
        
        {/* Badge circle */}
        <div className={`${sizes.badge} rounded-full bg-gradient-to-br ${colorClass} flex items-center justify-center shadow-lg`}>
          <Icon className={`${sizes.icon} text-white drop-shadow-sm`} />
        </div>
      </div>
      <div className="text-center">
        <p className="text-xs font-semibold">{badge.name}</p>
        {showDate && badge.earned_at && (
          <p className="text-[10px] text-muted-foreground">
            {new Date(badge.earned_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

export const BadgeList = ({ badges, earned = true, size = "md" }) => {
  if (!badges || badges.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <Award className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No badges yet</p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-4 justify-center">
      {badges.map((badge) => (
        <BadgeCard 
          key={badge.id} 
          badge={badge} 
          earned={earned}
          showDate={earned}
          size={size}
        />
      ))}
    </div>
  );
};

export const BadgeShowcase = ({ earnedBadges = [], availableBadges = [] }) => {
  return (
    <div className="space-y-6">
      {/* Earned Badges */}
      <div>
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Earned ({earnedBadges.length})
        </h4>
        {earnedBadges.length > 0 ? (
          <BadgeList badges={earnedBadges} earned={true} size="md" />
        ) : (
          <div className="text-center py-6 text-muted-foreground border border-dashed rounded-2xl">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Complete lessons to earn badges!</p>
          </div>
        )}
      </div>
      
      {/* Available Badges */}
      {availableBadges.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            Available ({availableBadges.length})
          </h4>
          <BadgeList badges={availableBadges} earned={false} size="sm" />
        </div>
      )}
    </div>
  );
};
